/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import { useSetAtom } from 'jotai'
import { useHydrateAtoms } from 'jotai/utils'
import React, { useEffect } from 'react'
import { useMediaQuery } from 'react-responsive'
import { DesktopHeader } from '@/components/e-commerce/header/DesktopHeader'
import { HeaderNavigationBarItem } from '@/components/e-commerce/header/links'
import { MobileHeader } from '@/components/e-commerce/header/MobileHeader'
import { navigationItemsAtom } from '@/store/navigation-items'

/** A description of the available properties for this component. */
type HeaderProps = {
  navigationItems: Array<HeaderNavigationBarItem>
}

/**
 * The main header component for the e-commerce website.
 * It renders either a desktop or mobile version based on screen size.
 */
const Header: React.FC<HeaderProps> = ({ navigationItems }) => {
  useHydrateAtoms([[navigationItemsAtom, navigationItems]])
  const setNavigationItems = useSetAtom(navigationItemsAtom)

  /**
   * Update the navigation atom when the story changes (as a result of client side navigation or other changes).
   */
  useEffect(() => {
    setNavigationItems(navigationItems)
  }, [navigationItems, setNavigationItems])

  const isMobile = useMediaQuery({ maxWidth: 800 })
  return isMobile ? <MobileHeader /> : <DesktopHeader />
}

export { Header }
